import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

interface DisciplineRow {
  discipline:  string
  not_started: number
  in_progress: number
  completed:   number
  blocked:     number
  total:       number
  pct:         number
}

interface Props {
  data:    DisciplineRow[]
  loading: boolean
}

const STATUS_COLORS = {
  not_started: '#D1D5DB',
  in_progress: '#FACC15',
  completed:   '#2563EB',
  blocked:     '#DC2626',
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?:  string
}) {
  if (!active || !payload?.length) return null
  const total = payload.reduce((s, p) => s + p.value, 0)
  const nameMap: Record<string, string> = { not_started: 'No iniciada', in_progress: 'En curso', completed: 'Finalizada', blocked: 'Bloqueada' }
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs min-w-[180px]">
      <p className="font-semibold text-gray-800 mb-2 truncate">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-3 py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
            <span className="text-gray-500">{nameMap[p.name] ?? p.name}</span>
          </span>
          <span className="font-semibold text-gray-900">{p.value}</span>
        </div>
      ))}
      <div className="border-t border-gray-100 mt-1.5 pt-1.5 flex justify-between">
        <span className="text-gray-500">Total</span>
        <span className="font-bold">{total}</span>
      </div>
    </div>
  )
}

function shortLabel(discipline: string) {
  const map: Record<string, string> = {
    'Civil': 'Civil', 'Mecánica': 'Mec.', 'Módulos': 'Mód.',
    'Eléctrica BT': 'Eléc.BT', 'Eléctrica MT': 'Eléc.MT',
    'Alta Tensión': 'AT', 'Líneas Evacuación': 'L.Evac.',
    'Subestaciones': 'SET', 'CCTV/Seguridad': 'CCTV',
    'Inst. Temporales': 'IT', 'Edificios': 'Edif.',
  }
  return map[discipline] ?? discipline.substring(0, 6)
}

export default function ProgressByDisciplineChart({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="card p-5">
        <div className="h-5 w-44 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="h-72 bg-gray-100 rounded animate-pulse" />
      </div>
    )
  }

  const chartData = data.map(d => ({
    name:        shortLabel(d.discipline),
    fullName:    d.discipline,
    not_started: d.not_started,
    in_progress: d.in_progress,
    completed:   d.completed,
    blocked:     d.blocked,
  }))

  return (
    <div className="card p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Avance por Disciplina</h3>
        <p className="text-xs text-gray-400 mt-0.5">Actividades por estado</p>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
          <Legend
            iconType="square" iconSize={8}
            formatter={v => {
              const m: Record<string, string> = { not_started: 'No iniciada', in_progress: 'En curso', completed: 'Finalizada', blocked: 'Bloqueada' }
              return <span className="text-xs text-gray-600">{m[v] ?? v}</span>
            }}
          />
          <Bar dataKey="not_started" stackId="a" fill={STATUS_COLORS.not_started} radius={[0,0,0,0]} />
          <Bar dataKey="in_progress" stackId="a" fill={STATUS_COLORS.in_progress} />
          <Bar dataKey="completed"   stackId="a" fill={STATUS_COLORS.completed} />
          <Bar dataKey="blocked"     stackId="a" fill={STATUS_COLORS.blocked} radius={[3,3,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
